import { atom } from 'jotai';

import type {
  ChronicleError,
  ChronicleLastStopReason,
  FirmwareFileInfo,
  FirmwareUpdateType,
  OtaStatus,
} from '@/tauri/chronicle/types';

/**
 * Chronicle connection FSM values.
 *
 * NOTE: `ERROR` is currently conflated into this FSM but is really a UX
 * state (the error modal is showing a failure message), not a transport
 * state. This conflation is the root cause of several APP-4895 bugs
 * (sticky ERROR after dismiss, button disabled at startup with no modal,
 * etc.). The long-term fix lives in APP-4884 / APP-4882 which moves the
 * error reason onto the event payload and removes ERROR from the FSM.
 *
 * Until then, **never read `connectionStatus === ERROR` directly in UI
 * code**. Subscribe to `reconnectButtonStateAtom` instead — it centralizes
 * the UX interpretation in one place.
 */
export const CONNECTION_STATUS = {
  DISCONNECTED: 'disconnected',
  SCANNING: 'scanning',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
} as const;

export type ConnectionStatus = (typeof CONNECTION_STATUS)[keyof typeof CONNECTION_STATUS];

export interface ChronicleDeviceState {
  connectionStatus: ConnectionStatus;
  /**
   * Last connected BLE peripheral identifier (iOS `lastConnectedBLEIdentifier`).
   * Not a hardware-device row. GET/DELETE use `linkedDeviceId`.
   */
  deviceId: string | null;
  deviceName: string | null;
  serialNumber: string | null;
  batteryLevel: number | null;
  /**
   * BLE signal strength in dBm (APP-6285). Live connected read on macOS
   * (refreshed at connect + the 60s health tick), so BLE command operations
   * can be analysed against link quality / device distance. `null` until read
   * / on Windows (WinRT exposes no connected-RSSI API).
   */
  rssi: number | null;
  /**
   * Advertisement RSSI in dBm captured during the scan that preceded this
   * connection (APP-6285). Broadcast-scale (~10-15 dB higher than `rssi`), not
   * refreshed while connected — but the ONLY RSSI available on Windows. `null`
   * when no scan preceded (e.g. cached auto-connect).
   */
  scanRssi: number | null;
  firmwareVersion: string | null;
  latestFirmwareVersion: string | null;
  firmwareUpdateAvailable: boolean;
  firmwareUpdateType: FirmwareUpdateType | null;
  latestFirmwareDownloadUrl: string | null;
  firmwareWhatsNew: string | null;
  firmwareUpdateInProgress: boolean;
  firmwareUpdateProgress: number | null;
  firmwareUpdateStatus: OtaStatus | null;
  firmwareUpdateError: { code: string; message: string; retryable: boolean } | null;
  selectedFirmwareFile: FirmwareFileInfo | null;
  error: ChronicleError | null;
  usbModeEnabled: boolean;
  /**
   * Auto power-off timer in minutes from the Remote.
   * `null` = unknown (hide UI); `0` = Off.
   */
  autoPowerOffMinutes: number | null;
  /**
   * Whether the Remote battery is charging (`MCU&CHARGE&{0|1}`).
   * `null` = unknown / unsupported firmware (hide glyph); `true` shows the icon.
   */
  isCharging: boolean | null;
  /** Total storage capacity in MB, reported by `APP&SPACE`. */
  storageTotalMb: number | null;
  /**
   * Free storage in MB, reported by `APP&SPACE` (`MCU&SPA&{free}&{total}`).
   * APP-6285 loop diagnostic — a near-full / never-freeing disk is a leading
   * explanation for an endless re-sync.
   */
  storageFreeMb: number | null;
  /** `true` when the device was already recording before the app connected (FR-035b). */
  recordingPreExisting: boolean;
  /**
   * Whether the firmware supports the chunked-session BLE protocol (derived
   * from `firmwareVersion` vs `MIN_CHUNKED_FW`, APP-4825). Tells telemetry
   * which sync path a looping device took. Defaults `false` until probed.
   */
  supportsChunkedSessions: boolean;
  /**
   * Why the most recent on-device stop happened (`max_duration` after
   * `MCU&REC&MAXDUR`, `poweroff_batlow` when the `MCU&STO` followed
   * `MCU&PWROFF&BATLOW`). Sticky until the next recording starts.
   */
  lastStopReason: ChronicleLastStopReason | null;
}

export interface ChronicleConnectionContext {
  lastDisconnectReason: 'manual' | 'unexpected' | 'poweroff_batlow' | null;
  autoReconnectUntil: number | null;
  autoReconnectAttempts: number;
  isErrorModalOpen: boolean;
  isReconnecting: boolean;
  /**
   * MongoDB `_id` from `HardwareDevice.id` (iOS `primaryLinkedDeviceId`).
   * GET/DELETE `/hardware-device/{id}` only. Never the BLE peripheral UUID
   * and never the deprecated API `device_id`.
   */
  linkedDeviceId: string | null;
  /** True while first-time pairing POST is in-flight (after BLE connect, before API response). */
  isLinkingDevice: boolean;
}

export const initialDeviceState: ChronicleDeviceState = {
  connectionStatus: CONNECTION_STATUS.DISCONNECTED,
  deviceId: null,
  deviceName: null,
  serialNumber: null,
  batteryLevel: null,
  rssi: null,
  scanRssi: null,
  firmwareVersion: null,
  latestFirmwareVersion: null,
  firmwareUpdateAvailable: false,
  firmwareUpdateType: null,
  latestFirmwareDownloadUrl: null,
  firmwareWhatsNew: null,
  firmwareUpdateInProgress: false,
  firmwareUpdateProgress: null,
  firmwareUpdateStatus: null,
  firmwareUpdateError: null,
  selectedFirmwareFile: null,
  error: null,
  usbModeEnabled: false,
  autoPowerOffMinutes: null,
  isCharging: null,
  storageTotalMb: null,
  storageFreeMb: null,
  recordingPreExisting: false,
  supportsChunkedSessions: false,
  lastStopReason: null,
};

const initialConnectionContext: ChronicleConnectionContext = {
  lastDisconnectReason: null,
  autoReconnectUntil: null,
  autoReconnectAttempts: 0,
  isErrorModalOpen: false,
  isReconnecting: false,
  linkedDeviceId: null,
  isLinkingDevice: false,
};

export const chronicleDeviceStateAtom = atom<ChronicleDeviceState>(initialDeviceState);

export const chronicleConnectionContextAtom = atom<ChronicleConnectionContext>(
  initialConnectionContext
);

export const chronicleConnectionStatusAtom = atom(
  (get) => get(chronicleDeviceStateAtom).connectionStatus
);

export const isChronicleConnectedAtom = atom(
  (get) => get(chronicleDeviceStateAtom).connectionStatus === CONNECTION_STATUS.CONNECTED
);

// Narrow selector for the bulk-sync device key — the identity the Rust side
// keys the offline-cache dir + sync-tracking rows on. Mirrors the
// `serialNumber ?? deviceName` fallback the auto-sync path provides
// (`chronicle-bulk-sync-provider`), so a manual retry resumes the SAME tracking
// rows the (failed) auto-sync created — including legacy device records where
// serial extraction yields null but the advertised name is present (APP-6284).
// Reading this narrow selector (vs the whole device-state atom) avoids
// re-rendering on every battery/RSSI/firmware tick.
export const chronicleBulkSyncDeviceKeyAtom = atom((get) => {
  const { serialNumber, deviceName } = get(chronicleDeviceStateAtom);
  return serialNumber ?? deviceName;
});

/**
 * Reason code carried while Rust auto-reconnect keeps running (e.g. the
 * radio is off, or a manual attempt failed under an active background arm).
 * Narrow selector so subscribers don't re-render on battery/RSSI ticks.
 */
export const chronicleReconnectingReasonAtom = atom((get) => {
  const { connectionStatus, error } = get(chronicleDeviceStateAtom);
  return connectionStatus === CONNECTION_STATUS.RECONNECTING && error ? error.code : null;
});

const LOW_BATTERY_THRESHOLD_IN_PERCENT = 20;

export const chronicleLowBatteryAtom = atom((get) => {
  const state = get(chronicleDeviceStateAtom);
  // `null` = not yet read (Rust now sends no placeholder), so a genuine 0%
  // battery correctly counts as low (PR #12284 review follow-up).
  return (
    state.connectionStatus === CONNECTION_STATUS.CONNECTED &&
    state.batteryLevel !== null &&
    state.batteryLevel <= LOW_BATTERY_THRESHOLD_IN_PERCENT
  );
});

export const resetChronicleDeviceStateAtom = atom(null, (_get, set) => {
  set(chronicleDeviceStateAtom, initialDeviceState);
});

export const resetChronicleConnectionContextAtom = atom(null, (_get, set) => {
  set(chronicleConnectionContextAtom, initialConnectionContext);
});

/**
 * Tracks whether a user-initiated `forceReconnect` IPC is currently in
 * flight (APP-4895). Single source of truth — replaces the previous mix
 * of a `useRef` guard and a mirrored `useState` flag in
 * `useChronicleConnect`.
 *
 * - `useChronicleConnect.reconnect` synchronously reads/writes via
 *   `store.get`/`store.set`. Same-tick double-clicks see the latest value
 *   immediately (Jotai writes are synchronous), so this also acts as the
 *   double-click guard — no separate ref needed.
 * - UI subscribes via `useAtomValue` (or via the derived
 *   `reconnectButtonStateAtom`) to keep the button disabled for the full
 *   IPC window, independent of intermediate Rust state-event timing.
 */
export const manualReconnectPendingAtom = atom(false);

/**
 * User cancel (`disconnect` IPC) in flight. A cancel issued mid-dial queues
 * behind the running attempt on the single-lane actor — this keeps the UI
 * in an acknowledged "Cancelling" state until the IPC settles.
 */
export const cancelReconnectPendingAtom = atom(false);

/**
 * Single derived view of the Reconnect button's UX state (APP-4895).
 *
 * Five values:
 *   - `idle`            — user can click to attempt a reconnect
 *   - `busy`            — a reconnect attempt is in progress (manual IPC
 *                         in-flight OR Rust reports a transient
 *                         scanning/connecting state)
 *   - `reconnecting`    — Rust-driven auto-reconnect is running. Unlike
 *                         `busy` this can last indefinitely (the native
 *                         macOS arm parks a no-timeout connect and never
 *                         gives up)
 *   - `cancelling`      — a user cancel (`disconnect` IPC) is in flight;
 *                         it can queue behind a running dial on the
 *                         single-lane actor, so the UI acknowledges with
 *                         a Cancelling state instead of going quiet
 *   - `owned-by-modal`  — the error modal is up; the modal owns the retry
 *                         CTA, so the underlying card button must not be
 *                         a duplicate clickable surface
 *
 * Centralizes the boolean composition that previously lived inline in
 * `device-card.tsx`. Adding a new disable condition (e.g. OTA in
 * progress) is a one-line atom edit; the UI never changes shape.
 *
 * The `connectionStatus === ERROR` interpretation lives **only** here —
 * see the `CONNECTION_STATUS` doc comment for why this is the only
 * acceptable place to read it from.
 */
export const reconnectButtonStateAtom = atom<
  'idle' | 'busy' | 'reconnecting' | 'cancelling' | 'owned-by-modal'
>((get) => {
  if (get(cancelReconnectPendingAtom)) return 'cancelling';
  if (get(manualReconnectPendingAtom)) return 'busy';

  const { connectionStatus } = get(chronicleDeviceStateAtom);
  if (
    connectionStatus === CONNECTION_STATUS.SCANNING ||
    connectionStatus === CONNECTION_STATUS.CONNECTING
  ) {
    return 'busy';
  }

  if (connectionStatus === CONNECTION_STATUS.RECONNECTING) {
    return 'reconnecting';
  }

  if (connectionStatus === CONNECTION_STATUS.ERROR) {
    const { isErrorModalOpen } = get(chronicleConnectionContextAtom);
    if (isErrorModalOpen) return 'owned-by-modal';
  }

  return 'idle';
});

/** Controls the Chronicle onboarding modal visibility. */
export const chronicleOnboardingModalOpenAtom = atom(false);

/** Controls the Chronicle quick start guide modal visibility. */
export const chronicleQuickStartModalOpenAtom = atom(false);

/** Controls the Chronicle USB-C enable prompt modal visibility. */
export const chronicleUsbEnableModalOpenAtom = atom(false);
